import { Box } from "@mui/material";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import useStartTestGround from "./useStartTestGround";
import {
  StyledAiMessage,
  StyledMessageTime,
  StyledSystemMessage,
  StyledUserMessage,
  StyledUserOptionsMessage,
} from "./TestGround.styles";
import { formatDate } from "features/helpers/utils";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import useNavigateEntity from "hooks/useNavigateEntity";
import MessageItem from "./MessageItem";
import useGetPagePermission from "hooks/useGetPagePermission";
import LoadingIcon from "components/LoadingComponent/LoadingIcon";
import { selectRemindBarShow } from "features/metadesk/redux/globalState/globalState.selector";
import useGetTrainingData from "../trainingArea/useGetTrainingData";

export default function TestGround({
  isTrainingArea = false,
  canUpdateResponse = false,
  handleRefine = () => {},
  handleRefetchTrainingData = () => {},
}) {
  const { formatMessage } = useIntl();
  const messagesContainerRef = useRef(null);
  const {
    messagesList,
    setMessagesList,
    canSend,
    setCanSend,
    message,
    setMessage,
    handleSendMessage,
    handleRestart,
    hasKnowledgeBase,
  } = useStartTestGround();

  const { fetchData } = useGetTrainingData();

  useEffect(() => {
    // 每次訊息列表改變時，自動把容器 scrollTop 設定為 scrollHeight
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [messagesList]);

  const { navigateEntity } = useNavigateEntity();
  const pagePermission = useGetPagePermission();
  const { readonly, canEdit } = pagePermission("aiManagement");
  const remindBarShow = useSelector(selectRemindBarShow);

  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Box borderRadius="4px" sx={{ flex: 1, overflow: "hidden" }}>
      {/* test ground */}
      <Box
        bgcolor="#fff"
        className="item-flex"
        justifyContent="space-between"
        p="15.5px 12px"
      >
        <Box className="item-flex">
          <Box mr="4px" color="#555" fontWeight={700}>
            {formatMessage({ id: "global.function.testGround" })}
          </Box>
          {!hasKnowledgeBase && !isTrainingArea && (
            <IconTooltip
              content={formatMessage({
                id: "global.chat.tooltip.noKnowledgeBase",
              })}
              customColor="#FC5555"
            />
          )}
          {isTrainingArea && (
            <IconTooltip
              content={formatMessage({
                id: "trainingArea.testGround.tooltip",
              })}
            />
          )}
        </Box>
        {canEdit && (
          <>
            {canUpdateResponse || syncLoading ? (
              <Box ml="4px" fontSize={12} fontWeight={500} color="#FC5555">
                {formatMessage({ id: "trainingArea.testGround.updatedHint" })}
                <Box
                  ml="4px"
                  component="span"
                  fontWeight={700}
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={() => {
                    setSyncLoading(true);
                    handleRefine();
                    setTimeout(() => {
                      setSyncLoading(false);
                    }, 3000);
                  }}
                >
                  {formatMessage({ id: "trainingArea.testGround.syncBtn" })}
                </Box>
              </Box>
            ) : (
              <Box
                className="item-flex"
                color="#7B61FF"
                px="8px"
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleRestart();
                }}
              >
                <i className="meta-crm-icon-ic_refresh_up font-size-12" />
                <Box ml="4px" fontSize={12} fontWeight={700} marginTop={"2px"}>
                  {formatMessage({ id: "testGround.btn.restart" })}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Box
        bgcolor="#EDEDED"
        height={`calc(100vh - ${remindBarShow ? 242 : 216}px)`}
        py="16px"
        overflow="auto"
      >
        {!hasKnowledgeBase && isTrainingArea ? (
          <Box
            height="100%"
            width="100%"
            px="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box textAlign="center">
              {formatMessage(
                { id: "trainingArea.testGround.placeholder" },
                {
                  knowledgeBase: (
                    <Box
                      component="span"
                      onClick={() =>
                        navigateEntity("/settings/ai/knowledgeBase")
                      }
                      color="#7B61FF"
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      px="4px"
                    >
                      {formatMessage({ id: "global.function.knowledgeBase" })}
                    </Box>
                  ),
                }
              )}
            </Box>
          </Box>
        ) : (
          <Box
            mx="auto"
            width="352px"
            borderRadius="17px"
            overflow="hidden"
            position="relative"
          >
            <Box
              sx={{
                bgcolor: "#7B61FF",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                fontWeight: 700,
                height: "56px",
              }}
            >
              <i
                className="meta-crm-icon-ic_arrow-left font-size-20"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                }}
              />
              <Box>{formatMessage({ id: "global.chat.title" })}</Box>
            </Box>
            <Box position="relative">
              {syncLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.5)",
                    zIndex: 2,
                  }}
                >
                  <LoadingIcon />
                </Box>
              )}
              <Box
                bgcolor="#fff"
                height={`calc(100vh - ${remindBarShow ? 389 : 363}px)`}
                overflow="auto"
                ref={messagesContainerRef}
              >
                <Box p="14.5px">
                  {messagesList.map((item, index) => (
                    <MessageItem
                      item={item}
                      index={index}
                      messagesList={messagesList}
                      handleSendMessage={handleSendMessage}
                      handleRefetchTrainingData={handleRefetchTrainingData}
                    />
                  ))}

                  {messagesList[messagesList.length - 1]?.isAdminResponse &&
                    !messagesList[messagesList.length - 1]?.isTexting && (
                      <Box
                        display="flex"
                        alignItems="flex-end"
                        flexDirection="column"
                        mt="14px"
                      >
                        <StyledUserOptionsMessage
                          onClick={() => {
                            setCanSend(false);
                            setMessagesList((prev) => [
                              ...prev,
                              {
                                text: "Your request is being transferred to a human agent. Please wait while we connect you.",
                                role: "system",
                                time: new Date(),
                              },
                            ]);
                          }}
                        >
                          {formatMessage({ id: "global.btn.getHumanAgent" })}
                        </StyledUserOptionsMessage>
                        <StyledUserOptionsMessage>
                          {formatMessage({ id: "global.btn.closeTicket" })}
                        </StyledUserOptionsMessage>
                      </Box>
                    )}
                </Box>
              </Box>
              <Box
                bgcolor="#fff"
                height="56px"
                p="17px 14px 17px 21px"
                className="item-flex"
                justifyContent="space-between"
                borderTop="1px solid #E6E6E6"
              >
                <input
                  placeholder={formatMessage({
                    id: "global.chat.input.placeholder",
                  })}
                  style={{ flex: 1 }}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.keyCode === 13 && canSend) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendMessage(message);
                    }
                  }}
                />
                <Box className="item-flex" flexShrink={0}>
                  <i
                    className="meta-crm-icon-ic_img font-size-20"
                    style={{ marginLeft: "10.5px", color: "#A5A5A5" }}
                  />
                  <Box ml="10.5px">
                    <Box
                      width={20}
                      height={20}
                      bgcolor={readonly ? "#A5A5A5" : "#7B61FF"}
                      borderRadius={10}
                      className="item-flex"
                      justifyContent={"center"}
                      onClick={() => handleSendMessage(message)}
                    >
                      <i
                        className="meta-crm-icon-ic_arrowForward font-size-14"
                        style={{ transform: "rotate(-90deg)", color: "#fff" }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
